/**
 * Dependencies.
 */
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

import { commentTypeService } from '@/api/delivery-survey-comment/comment-type.service';
import { entityCommentTypeService } from '@/api/delivery-survey-entity-comment/entity-comment-type.service';
import OrderSurveyModel from '@/api/delivery-survey/delivery-survey.model';
import { deliverySurveyService as orderSurveyService } from '@/api/delivery-survey/delivery-survey.service';

/**
 * Mixin.
 */
@Component({
  name: 'survey-mixin'
})
export default class extends Vue {
  /**
   * Property `commentsTypes`.
   */
  commentsTypes = [];

  /**
   * Property `entityCommentsTypes`.
   */
  entityCommentsTypes = [];

  /**
   * Property `numericKeysAllowed`.
   */
  numericKeysAllowed: string[] = [...Array(10).keys()].map(item =>
    String(item)
  );

  /**
   * Property `unhappy`.
   */
  get unhappy() {
    const unhappy = [];

    this.commentsTypes.forEach(({ code, level, name }) => {
      if (level.includes('1') || level.includes('2') || level.includes('3')) {
        unhappy.push({
          id: code,
          label: name
        });
      }
    });

    return unhappy;
  }

  /**
   * Property `satisfied`.
   */
  get satisfied() {
    const satisfied = [];

    this.commentsTypes.forEach(({ code, level, name }) => {
      if (level.includes('4')) {
        satisfied.push({
          id: code,
          label: name
        });
      }
    });

    return satisfied;
  }

  /**
   * Property `amazed`.
   */
  get amazed() {
    const amazed = [];

    this.commentsTypes.forEach(({ code, level, name }) => {
      if (level.includes('5')) {
        amazed.push({
          id: code,
          label: name
        });
      }
    });

    return amazed;
  }

  /**
   * Getter `dislike`.
   */
  get dislike() {
    const dislike = [];

    this.entityCommentsTypes.forEach(({ code, level, name }) => {
      if (level.includes('DISLIKE')) {
        dislike.push({
          id: code,
          label: name
        });
      }
    });

    return dislike;
  }

  /**
   * Getter `like`.
   */
  get like() {
    const like = [];

    this.entityCommentsTypes.forEach(({ code, level, name }) => {
      if (
        (level.includes('LIKE') && !level.includes('DISLIKE')) ||
        level.replace(/\s/g, '').includes('LIKE,') ||
        level.replace(/\s/g, '').includes(',LIKE')
      ) {
        like.push({
          id: code,
          label: name
        });
      }
    });

    return like;
  }

  /**
   * Handle get types.
   */
  async handleGetTypes() {
    await this.handleGetCommentTypes();
    await this.handleGetEntityCommentTypes();
  }

  /**
   * Handle get comment types.
   */
  async handleGetCommentTypes() {
    try {
      this.commentsTypes = await commentTypeService.get();
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleGetCommentTypes' });
      }
    }
  }

  /**
   * Handle get entity comment types.
   */
  async handleGetEntityCommentTypes() {
    try {
      this.entityCommentsTypes = await entityCommentTypeService.get();
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleGetEntityCommentTypes' });
      }
    }
  }

  /**
   * handle get client rating level.
   */
  handleGetClientRatingLevel({ rating }: { rating: number }) {
    let level = null;

    switch (rating) {
      case 5:
        level = 'amazed';
        break;

      case 4:
        level = 'satisfied';
        break;

      default:
        level = 'unhappy';
        break;
    }

    return level;
  }

  /**
   * Handle get order survey.
   */
  async handleGetOrderSurvey(): Promise<OrderSurveyModel> {
    const { key } = this.$route?.params || {};

    let survey = null;

    try {
      survey = await orderSurveyService.getById(key);
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleGetOrderSurvey' });
      }

      this.$router.replace({ name: 'NotFound' });
    }

    return survey;
  }
}
